'use client';
import { methods, methodsText } from '@lib/constants/methods';
import { AjustedVariant } from '@modules/products/templates';
import { BusinessInfo } from 'types/business';
import { InputTextArea } from '.';
import InputQuantity from './input-quantity';
import MediumGiftCardAmount from './giftcard-amount';
import DeliveryDate from './giftcard-delivery-date';
import { DeleveryMethods } from './giftcard-delivery-method';
import FromFields from './giftcard-from-fields';
import MediumGiftCardInformation from './giftcard-information';
import ReviewCheckoutButtons from './giftcard-review-checkout-buttons';
import ToFields from './giftcard-to-fields';
import { P } from '@modules/common/components/typography/P';
import { Label } from '@modules/common/components/form/label/Label';
import { useFormikContext } from 'formik';
import { isMobileDevices } from '@lib/util/is-mobile-devices';
import { useContext, useEffect, useState, useRef } from 'react';
import { checkSpecialCharMessage } from '../validation/validateKeyEntry';
import { GIFT_CARD_MESSAGE_LENGTH } from '@lib/constants/gift-card-constant';
import { CartDropdownContext } from '@lib/context/cart-dropdown-context';
import { currencyToDisplayPoints } from '@lib/util/parser';
import { CARD_PROGRAMS, CUSTOMER_SERVICE_PHONE_NUMBER, CUSTOMER_SERVICE_PHONE_NUMBER_TEL, DEFAULT_SEARCH_PROVIDER } from '@lib/constants';
import { Imagery } from '@modules/home/components/imagery/imagery';
import { formatAmount, useCart } from 'medusa-react';
import { domains } from '@lib/constants/domains';
import { useImageBySearch } from '@lib/hooks/use-image-by-search';
import { PersonTag } from '@modules/common/components/person-tag';
import { RESTAURANTS_IMG_TITLE, RESTAURANTS_IMG_URL } from '@lib/constants/gift-card-type-constants';
import letThemChooseUtil from '@lib/util/let-them-choose-util';

type Props = {
  business: BusinessInfo;
  variants: AjustedVariant[];
  customVariants: AjustedVariant[];
  isOptedOut: boolean;
  isClickSubmit: boolean;
  setIsClickSubmit: (isClickSubmit: boolean) => void;
  setIsTextMethod: (isTextMethod: boolean) => void;
};

const GiftCardTemplate = (props: Props) => {
  const [imageTitle, setImageTitle] = useState(RESTAURANTS_IMG_TITLE);
  const [imageUrl, setImageUrl] = useState(RESTAURANTS_IMG_URL);
  const [isDisabledButton, setIsDisabledButton] = useState(false);
  const [tempOptedOutPhone, setTempOptedOutPhone] = useState('');
  const [hasScrolled, setHasScrolled] = useState(false);
  const imageContainerRef = useRef<HTMLDivElement>(null);

  const enableScheduleDelivery: boolean = process.env.NEXT_PUBLIC_ENABLE_SCHEDULE_DELIVERY === 'true';
  const { business, variants, customVariants, isOptedOut, isClickSubmit, setIsClickSubmit, setIsTextMethod } = props;
  const form = useFormikContext<any>();
  const deliveryMethod = form.getFieldProps('deliveryMethod');
  const amount = form.getFieldProps('variant');
  const cartContext = useContext(CartDropdownContext);
  const { cart } = useCart();
  const { imageSrc } = useImageBySearch(business.image_url, business.search_provider || DEFAULT_SEARCH_PROVIDER, 860);
  const sanitizePhone = (phone: string): string => {
    let result = phone
      .replace(/[^\d]/g, '')
      .replace(/^[01]+/, '')
      .substring(0, 10); //Max 10 numbers
    return result;
  };

  const sanitizeName = (message: string): string => {
    let result = message.replace(/[^A-Za-zÀ-ÖØ-öø-ÿ ‘.-]+/g, '');
    return result;
  };

  const sanitizeMessage = (message: string): string => {
    let result = message
      .replace(/[^0-9A-Za-zÀ-ÖØ-öø-ÿ' ,#()/.$%&@*!/?+=_:;"\\-]/g, '')
      .substring(0, GIFT_CARD_MESSAGE_LENGTH); //Max 250 characters;
    return result;
  };

  const sanitizeEmail = (message: string): string => {
    if (message.includes('@')) {
      const localPartAllowed = /[^\w.!#$%&'*+/=?^_`{|}~-]/g; // Matches unsupported characters in the local part
      const domainPartAllowed = /[^\w.-]/g; // Matches unsupported characters in the domain part

      // Split the email into local and domain parts
      let [localPart, domainPart] = message.split('@');

      // Remove unsupported characters from each part
      localPart = localPart.replace(localPartAllowed, '');
      domainPart = domainPart.replace(domainPartAllowed, '');

      // Return the cleaned email
      return `${localPart}@${domainPart}`;
    } else {
      let result = message.replace(
        /[^0-9A-Za-zÀ-ÖØ-öø-ÿ' ,#()\/.$%&@*!?+=_:;"\\-]/g,
        ''
      );
      return result;
    }
  };

  const handlePastePhoneNumber = (event: any) => {
    event.preventDefault();
    let pasteText = event.clipboardData?.getData('text');
    pasteText = sanitizePhone(pasteText);
    if (!event.target) return;
    form?.setFieldValue(event.target.name, pasteText);
  };

  const handlePasteEmail = (event: any) => {
    event.preventDefault();
    let pasteText = event.clipboardData?.getData('text');
    pasteText = sanitizeEmail(pasteText);
    if (!event.target) return;
    form?.setFieldValue(event.target.name, pasteText);
  };

  const handlePasteToFrom = (event: any) => {
    event.preventDefault();
    let pasteText = event.clipboardData?.getData('text');
    pasteText = sanitizeName(pasteText);
    if (!event.target) return;
    form?.setFieldValue(event.target.name, pasteText);
  };

  const handlePasteMessage = (event: any) => {
    event.preventDefault();
    let pasteText = event.clipboardData?.getData('text');
    pasteText = sanitizeMessage(pasteText);
    if (!event.target) return;
    form?.setFieldValue(event.target.name, pasteText);
  };

  // Separate useEffect for scrolling - runs only once on component mount
  useEffect(() => {
    // Scroll to image on mobile devices after component mounts, but only once
    if (typeof window !== 'undefined' && isMobileDevices() && imageContainerRef.current && !hasScrolled) {
      // Small delay to ensure the page is fully rendered
      setTimeout(() => {
        imageContainerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        setHasScrolled(true);
      }, 100);
    }
  }, [hasScrolled]);

  // Original useEffect for other functionality
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const cardProgram = sessionStorage.getItem(CARD_PROGRAMS.KEY);
      const {title, img_url}=letThemChooseUtil.getGiftCardImageAndTitle(cardProgram??'');
      setImageTitle(title);
      setImageUrl(img_url);
    }

    if (
      deliveryMethod?.value !== methodsText.Text &&
      deliveryMethod?.value !== methodsText.Email
    ) {
      setIsDisabledButton(false);
      setIsTextMethod(false);
      return;
    }
    const toName = document.getElementsByName('toName');
    const fromName = document.getElementsByName('fromName');
    const message = document.getElementsByName('message');

    if (
      !message ||
      !message[0] ||
      !toName ||
      !toName[0] ||
      !fromName ||
      !fromName[0]
    )
      return;

    toName[0].onpaste = handlePasteToFrom;
    fromName[0].onpaste = handlePasteToFrom;
    message[0].onpaste = handlePasteMessage;

    if (deliveryMethod?.value == methodsText.Text) {
      const toPhones = document.getElementsByName('toPhone');
      const confirmToPhones = document.getElementsByName('toPhoneConfirm');
      setIsTextMethod(true);
      if (!toPhones || !toPhones[0] || !confirmToPhones || !confirmToPhones[0])
        return;

      toPhones[0].onpaste = handlePastePhoneNumber;
      confirmToPhones[0].onpaste = handlePastePhoneNumber;
      if (isClickSubmit || form.values.toPhone === tempOptedOutPhone) {
        setIsDisabledButton(isOptedOut);
        setIsClickSubmit(false);
        setTempOptedOutPhone(form.values.toPhone);
      } else {
        if (form.values.toPhone === ''
          || form.values.toPhoneConfirm === ''
          || form.values.toPhone !== form.values.toPhoneConfirm
          || tempOptedOutPhone !== form.values.toPhone
          || tempOptedOutPhone !== form.values.toPhoneConfirm) {
          setIsDisabledButton(false);
          setTempOptedOutPhone('');
        }
      }
    } else {
      setIsTextMethod(false);
      setIsDisabledButton(false);
      const toEmail = document.getElementsByName('toEmail');
      const confirmToEmail = document.getElementsByName('toEmailConfirm');

      if (!toEmail || !toEmail[0] || !confirmToEmail || !confirmToEmail[0])
        return;

      toEmail[0].onpaste = handlePasteEmail;
      confirmToEmail[0].onpaste = handlePasteEmail;
    }
  }, [deliveryMethod]);

  return (
    <div>
      <div className="container-page md:py-6">
        <div className="flex flex-col md:flex-row gap-5 w-full">
          <div ref={imageContainerRef} className="w-full md:w-[360px] md:flex-none lg:w-[460px]">
            <PersonTag place_id={business.id} author_display_name={business.author_display_name} />
            {business?.id ? <div
              className={`w-full pt-[55%] md:pt-[85%] relative ${imageSrc ? 'bg-cover' : 'bg-contain'} bg-no-repeat bg-center overflow-hidden rounded-[3px]`}
              style={{ backgroundImage: `url(${imageSrc || '/main-logo.png'})` }}
            >
              <img
                src={imageSrc || '/logo-gcm.png'}
                alt={business.name}
                className="w-full max-w-ful absolute top-0 opacity-0"
                style={{ objectFit: 'contain' }}
              />
            </div> :
              <div className='rounded border border-black shadow-input w-full p-4'>
                <Imagery
                  url={imageUrl}
                  title={imageTitle}
                  styleText="bg-pink_drink"
                  isChooseYourOwn={true}
                />
              </div>
            }
          </div>
          <div className="w-full">
            <MediumGiftCardInformation
              location={business.location}
              rating={business.rating}
              review_count={business.review_count}
              name={business.name}
              phone={business.phone}
              coordinates={business.coordinates}
              distance={business.distance}
              url={business.url}
              id={business.id}
              search_provider={business?.search_provider || DEFAULT_SEARCH_PROVIDER}
            />
            <div className="mt-12 md:mt-2">
              <div className="">
                <MediumGiftCardAmount variants={variants} customVariants={customVariants}/>
              </div>
              {cartContext?.pointMode ? (
                <P className="text-gray-ada mt-2 text-base font-normal">
                  <div className="flex items-center gap-2">
                    <div className="text-right text-base text-black font-medium">
                      Total:
                    </div>
                    <div className="flex flex-wrap">
                      {currencyToDisplayPoints(amount?.value?.prices || (variants?.length > 0 ? variants[0].prices : 0))}
                    </div>
                  </div>
                </P>
              ) : (
                <P className="text-gray-ada mt-2 text-base font-normal">
                  A convenience fee of {formatAmount({
                    amount: amount?.value?.fee || 0,
                    region: cart?.region || { currency_code: 'USD', tax_rate: 0, tax_code: 'USD' },
                    includeTaxes: false,
                  })} per card will be applied.
                </P>
              )}
              <div className="card__quantity hidden">
                <InputQuantity />
              </div>
              <div className="mt-7 md:mt-2">
                <div className="">
                  <div className="text-base text-black font-medium mb-2">
                    Delivery Method:
                  </div>
                  <DeleveryMethods methods={methods} />
                </div>
                <P className="text-gray-ada mt-3 text-base font-normal">
                  {deliveryMethod.value === 'Email' &&
                    'Gift Card Market will email a redemption URL and instructions to the Recipient’s email address.'}
                  {deliveryMethod.value === 'Text' && (
                    <>
                      {!isMobileDevices()
                        ? 'Gift Card Market will text a redemption URL and instructions to the Recipient’s phone number.'
                        : 'You will receive a link to text to the prepaid gift card Recipient from your phone number. They must validate their phone number before accessing their prepaid gift card.'}
                    </>
                  )}
                </P>
                <p className="text-gray-ada mt-3 text-base font-normal text-pink_drink">
                  {deliveryMethod.value === 'Text' && !isMobileDevices() && (
                    <>
                      *Best experience for sending via text is on your mobile device where you can send it from your phone number.
                    </>
                  )}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="bg-white md:py-6 px-4">
        <div className="w-full md:max-w-[550px] mx-auto">
          <div className="flex flex-col md:flex-row w-full md:gap-2">
            <div className="flex-none w-[127px] md:pt-3">
              <Label>To:</Label>
            </div>
            <div className="w-full mt-3">
              <ToFields />
              {
                deliveryMethod.value === 'Text' && !isMobileDevices() && (
                  <P className="text-gray-ada text-xs mt-1.5 mb-1.5">
                    By entering the recipient phone number you confirm that you have permission to send text messages to the mobile phone number entered and that the recipient consents to receive such messages. Message and data rates may apply.
                  </P>
                )
              }

            </div>
          </div>
          <div className="flex w-full flex-col mt-2 md:mt-0 md:flex-row md:gap-2">
            <div className="flex-none w-[127px] md:pt-3">
              <Label>From:</Label>
            </div>
            <div className="w-full  mt-3">
              <FromFields />
            </div>
          </div>
          {/*<div className='flex w-full gap-2 flex-col md:flex-row pb-3'>*/}
          {/*    <div className='flex-none md:w-[127px] pt-1'>*/}
          {/*        <Label>Relationship: <span*/}
          {/*            className='text-gray-ada text-base font-normal'>(optional)</span></Label>*/}
          {/*    </div>*/}
          {/*    <div className='w-full'>*/}
          {/*        <InputTextField label='Relationship' name='relationship' />*/}
          {/*    </div>*/}
          {/*</div>*/}
          {/*<div className='flex w-full gap-2 flex-col md:flex-row pb-3'>*/}
          {/*    <div className='flex-none md:w-[127px] pt-1'>*/}
          {/*        <Label>Occasion: <span*/}
          {/*            className='text-gray-ada text-base font-normal'>(optional)</span></Label>*/}
          {/*    </div>*/}
          {/*    <div className='w-full'>*/}
          {/*        <InputTextField label='Occasion' name='occasion' />*/}
          {/*    </div>*/}
          {/*</div>*/}
          <div className="flex w-full gap-2 flex-col md:flex-row">
            <div className="flex-none md:w-[127px] pt-1">
              <Label>
                Message:{' '}
                <span className="text-gray-ada text-base font-normal">
                  (optional)
                </span>
              </Label>
            </div>
            <div className="w-full">
              <InputTextArea
                maxLength={GIFT_CARD_MESSAGE_LENGTH}
                label="Message"
                onkeydown={checkSpecialCharMessage}
                placeholder={`Gift message will be applied to all cards within this selection.\n\nMessages containing derogatory language may be found to be inappropriate and will not be forwarded and/or deleted. \n\nLimit to ${GIFT_CARD_MESSAGE_LENGTH} characters.`}
                name={'message'}
              />
            </div>
          </div>
          {deliveryMethod.value === 'Email' && enableScheduleDelivery ? (
            <div className="flex w-full gap-2 flex-col md:flex-row">
              <div className="flex-none md:w-[127px] pt-1">
                <Label>Delivery Date:</Label>
              </div>
              <div className="w-full">
                <DeliveryDate textColor="#79ddcc" title="Set a Delivery Date" />
              </div>
            </div>
          ) : null}
          <div className="flex w-full gap-2 mt-5">
            <div className="flex-none w-[95px] md:w-[120px]">
              <Label>Review & Checkout:</Label>
            </div>
            <div className="w-full">
              <ReviewCheckoutButtons businessName={business.name} isDisabledButton={isDisabledButton} />
            </div>
          </div>
        </div>
      </div>
      <div className="container-page static-content mt-[35px] md:pt-10">
        <P>
          Give Local at The Gift Card Market – the most convenient way to send
          Local Prepaid Gift Cards in just minutes. You can send Local Prepaid
          Gift Cards via text, email, or print at home at{' '}
          <a href={domains.prod}>
            {domains.prod}.
          </a>{' '}
          Local Prepaid Gift Cards are ready for use immediately once redeemed.
          Funds never expire and there are no post purchase fees.
        </P>
        <P className="mt-5 break-words">
          Funds can be used at the selected merchant at locations in the United
          States. If you would like to switch the merchant of choice, you can do so on the Redemption page at no charge. Local Prepaid Gift Cards can be
          used in person or over the phone.{' '}
          <a href="https://app.akimbocard.com/app/#/akimbo/cardBalance">
            Click Here
          </a>{' '}
          to Check Balance on your Prepaid Gift Card.
        </P>
        <P className="mt-5">
          If you have any questions or need more information, please contact our
          support team at <a href={`tel:${CUSTOMER_SERVICE_PHONE_NUMBER_TEL}`} className='text-pink_drink'>{CUSTOMER_SERVICE_PHONE_NUMBER}</a>, visit us at{' '}
          <a href="/contact-us">{domains.prod}/contact-us</a> or
          reference our <a href="/faqs">FAQ’s</a>.
        </P>
      </div>
    </div>
  );
};

export default GiftCardTemplate;
